import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api-client.js";
import { ModuleCard } from "@/components/ui/ModuleCard.js";
import { SectionHeader } from "@/components/ui/SectionHeader.js";
import { Badge } from "@/components/ui/Badge.js";
import { ADMIN_SECTIONS, ADMIN_GROUPS, type AdminSection } from "./adminSections.js";

interface SystemStatusLite {
  database: { status: "ok" | "error" };
  counts: { projects: number; users: number; auditEntries: number; configuredSettings: number };
}

const GROUP_HINT: Record<AdminSection["group"], string | undefined> = {
  "Einstellungen": "produktiv · persistent",
  "Module & Status": undefined,
  "Geplant": "Architektur geplant — noch nicht aktiv",
};

export function AdminOverview() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SystemStatusLite | null>(null);

  useEffect(() => {
    void apiClient
      .get<SystemStatusLite>("/admin/system-status")
      .then((res) => {
        if (res.success) setStatus(res.data);
      })
      .catch(() => {
        /* still funktional ohne Live-Status */
      });
  }, []);

  const byGroup = (g: AdminSection["group"]) => ADMIN_SECTIONS.filter((s) => s.group === g);
  const dbOk = status?.database.status === "ok";

  return (
    <div>
      {ADMIN_GROUPS.map((group) => (
        <div key={group}>
          <SectionHeader title={group} hint={GROUP_HINT[group]} />
          <div className="grid grid-auto">
            {byGroup(group).map((s) => {
              if (s.kind === "planned") {
                return (
                  <ModuleCard
                    key={s.id}
                    icon={s.icon}
                    title={s.label}
                    description={s.description}
                    planned
                    badge={<Badge tone="neutral">geplant</Badge>}
                    fields={s.plannedFields}
                  />
                );
              }

              if (s.kind === "settings") {
                return (
                  <ModuleCard
                    key={s.id}
                    icon={s.icon}
                    title={s.label}
                    description={s.description}
                    cta="Konfigurieren →"
                    onClick={() => navigate(`/admin/${s.id}`)}
                  />
                );
              }

              if (s.kind === "status") {
                const live = status ? (
                  <>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: dbOk ? "var(--color-success)" : "var(--color-error)" }} />
                    DB {dbOk ? "ok" : "Fehler"} · {status.counts.projects} Projekte · {status.counts.configuredSettings} Settings
                  </>
                ) : undefined;
                return (
                  <ModuleCard
                    key={s.id}
                    icon={s.icon}
                    title={s.label}
                    description={s.description}
                    cta="Status ansehen →"
                    live={live}
                    onClick={() => navigate(`/admin/${s.id}`)}
                  />
                );
              }

              // module → deep link into the existing module
              return (
                <ModuleCard
                  key={s.id}
                  icon={s.icon}
                  title={s.label}
                  description={s.description}
                  cta="Modul öffnen ↗"
                  onClick={() => navigate(s.linkPath!)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
